import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Briefcase, Plus, UserPlus, Trash2, Phone, Building2, DollarSign, Users2, Activity, Clock, CheckCircle2, XCircle, LinkIcon } from "lucide-react";

export default function AccountingFirm() {
  const [tab, setTab] = useState("accountants");
  const [info, setInfo] = useState(null);
  const [accountants, setAccountants] = useState([]);
  const [services, setServices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accOpen, setAccOpen] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [asnOpen, setAsnOpen] = useState(false);
  const [asnForm, setAsnForm] = useState({ client_company_id: "", accountant_id: "", monthly_fee: 50, currency: "USD", note: "" });

  const load = async () => {
    try {
      const [i, a, s, c] = await Promise.all([
        api.get("/firm/info"), api.get("/firm/accountants"),
        api.get("/firm/services"), api.get("/companies"),
      ]);
      setInfo(i.data); setAccountants(a.data); setServices(s.data);
      setCompanies(c.data.filter(x => !x.is_admin_firm));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveAcc = async () => {
    if (!accForm.name.trim() || !accForm.email.trim() || !accForm.password.trim()) return toast.error("الاسم والإيميل وكلمة المرور مطلوبة");
    try {
      await api.post("/firm/accountants", accForm);
      toast.success("تم إضافة المحاسب");
      setAccOpen(false); setAccForm({ name: "", email: "", password: "", phone: "" }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const delAcc = async (id) => {
    if (!confirm("حذف المحاسب؟ سيتم إنهاء جميع خدماته النشطة.")) return;
    try { await api.delete(`/firm/accountants/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openAssign = () => {
    setAsnForm({ client_company_id: companies[0]?.id || "", accountant_id: accountants[0]?.id || "", monthly_fee: 50, currency: "USD", note: "" });
    setAsnOpen(true);
  };

  const saveAssign = async () => {
    if (!asnForm.client_company_id || !asnForm.accountant_id) return toast.error("اختر الشركة والمحاسب");
    try {
      await api.post("/firm/services", asnForm);
      toast.success("تم تعيين المحاسب للشركة");
      setAsnOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const endService = async (sid) => {
    if (!confirm("إنهاء الخدمة؟ سيتم إلغاء وصول المحاسب لهذه الشركة.")) return;
    try { await api.delete(`/firm/services/${sid}`); toast.success("تم إنهاء الخدمة"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const acceptPending = async (s) => {
    if (accountants.length === 0) return toast.error("أضف محاسباً أولاً");
    // convert pending → active by creating a new assignment and marking old as ended
    try {
      await api.delete(`/firm/services/${s.id}`);  // marks pending as ended
      await api.post("/firm/services", {
        client_company_id: s.client_company_id,
        accountant_id: accountants[0].id,
        monthly_fee: 50, currency: "USD", note: "تفعيل بعد طلب"
      });
      toast.success("تم قبول الطلب — يمكنك تغيير المحاسب لاحقاً");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const active = services.filter(s => s.status === "active");
  const pending = services.filter(s => s.status === "pending");
  const ended = services.filter(s => s.status === "ended");

  return (
    <div className="space-y-4" data-testid="firm-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Briefcase className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">شركة المحاسبة — إدارتك المركزية</h1>
          <p className="text-sm text-muted-foreground">{info?.name} · قدّم خدمة محاسبة بشرية لعملائك المشتركين وحوّلها إلى إيراد إضافي شهري</p>
        </div>
      </div>

      {info && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 border-primary/20 bg-primary/5">
            <div className="text-xs text-primary flex items-center gap-1"><Users2 className="w-3 h-3" /> عدد المحاسبين</div>
            <div className="text-2xl font-bold text-primary mt-1">{info.accountants_count}</div>
          </Card>
          <Card className="p-4 border-emerald-200 bg-emerald-50">
            <div className="text-xs text-emerald-700 flex items-center gap-1"><Activity className="w-3 h-3" /> خدمات نشطة</div>
            <div className="text-2xl font-bold text-emerald-900 mt-1">{info.active_services}</div>
          </Card>
          <Card className="p-4 border-amber-200 bg-amber-50">
            <div className="text-xs text-amber-700 flex items-center gap-1"><Clock className="w-3 h-3" /> طلبات معلقة</div>
            <div className="text-2xl font-bold text-amber-900 mt-1">{info.pending_requests}</div>
          </Card>
          <Card className="p-4 border-violet-200 bg-violet-50">
            <div className="text-xs text-violet-700 flex items-center gap-1"><DollarSign className="w-3 h-3" /> الإيراد الشهري المتوقع</div>
            <div className="text-2xl font-bold text-violet-900 mt-1">${active.reduce((s, x) => s + (+x.monthly_fee || 0), 0).toFixed(0)}</div>
          </Card>
        </div>
      )}

      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        {[["accountants", "المحاسبون", accountants.length], ["services", "الخدمات النشطة", active.length], ["pending", "طلبات معلقة", pending.length]].map(([k, l, n]) => (
          <button key={k} onClick={()=>setTab(k)}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition ${tab === k ? "bg-primary text-primary-foreground shadow" : "hover:bg-background"}`}
            data-testid={`firm-tab-${k}`}>
            {l} {n > 0 && <span className="ms-1 text-xs opacity-80">({n})</span>}
          </button>
        ))}
      </div>

      {tab === "accountants" && (
        <div>
          <div className="flex justify-end mb-2">
            <Button onClick={()=>setAccOpen(true)} className="gap-2" data-testid="add-accountant-btn"><UserPlus className="w-4 h-4" /> إضافة محاسب</Button>
          </div>
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>الاسم</TableHead><TableHead>الإيميل</TableHead><TableHead>الهاتف</TableHead>
                <TableHead>عدد الشركات المسندة</TableHead><TableHead>تاريخ الإضافة</TableHead><TableHead>إجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {accountants.map(a => (
                  <TableRow key={a.id} data-testid={`acc-row-${a.id}`}>
                    <TableCell className="font-bold">{a.name}</TableCell>
                    <TableCell className="text-xs font-mono" dir="ltr">{a.email}</TableCell>
                    <TableCell className="text-xs font-mono" dir="ltr">{a.phone || "—"}</TableCell>
                    <TableCell><span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-bold">{a.assignments}</span></TableCell>
                    <TableCell className="text-xs">{new Date(a.created_at).toLocaleDateString("ar-EG")}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={()=>delAcc(a.id)} className="h-7 text-destructive" data-testid={`del-acc-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {accountants.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد محاسبين — ابدأ بإضافة محاسب لتقديم الخدمة لعملائك</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {tab === "services" && (
        <div>
          <div className="flex justify-end mb-2">
            <Button onClick={openAssign} disabled={accountants.length === 0 || companies.length === 0} className="gap-2" data-testid="assign-service-btn"><LinkIcon className="w-4 h-4" /> تعيين محاسب لشركة</Button>
          </div>
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>الشركة</TableHead><TableHead>المحاسب المسند</TableHead><TableHead>الرسوم الشهرية</TableHead>
                <TableHead>بدأت في</TableHead><TableHead>ملاحظة</TableHead><TableHead>إجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {active.map(s => (
                  <TableRow key={s.id} data-testid={`svc-row-${s.id}`}>
                    <TableCell className="font-bold flex items-center gap-1"><Building2 className="w-3 h-3" /> {s.client_company_name}</TableCell>
                    <TableCell>{s.accountant_name}{s.accountant_phone && <div className="text-[10px] text-muted-foreground flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{s.accountant_phone}</div>}</TableCell>
                    <TableCell className="font-bold">{s.monthly_fee} {s.currency}</TableCell>
                    <TableCell className="text-xs">{s.started_at ? new Date(s.started_at).toLocaleDateString("ar-EG") : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.note || "—"}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={()=>endService(s.id)} className="h-7 text-destructive" data-testid={`end-svc-${s.id}`}>إنهاء</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {active.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد خدمات نشطة</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {tab === "pending" && (
        <div className="border border-border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>الشركة الطالبة</TableHead><TableHead>تاريخ الطلب</TableHead>
              <TableHead>ملاحظة العميل</TableHead><TableHead>إجراءات</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pending.map(s => (
                <TableRow key={s.id} data-testid={`pending-row-${s.id}`}>
                  <TableCell className="font-bold">{s.client_company_name}</TableCell>
                  <TableCell className="text-xs">{new Date(s.requested_at || s.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.note || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={()=>acceptPending(s)} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`accept-${s.id}`}><CheckCircle2 className="w-3 h-3" /> قبول</Button>
                      <Button size="sm" variant="ghost" onClick={()=>endService(s.id)} className="h-7 text-xs text-destructive" data-testid={`reject-${s.id}`}><XCircle className="w-3 h-3" /> رفض</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {pending.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">لا توجد طلبات معلقة</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={accOpen} onOpenChange={setAccOpen}>
        <DialogContent data-testid="acc-dialog">
          <DialogHeader><DialogTitle>محاسب جديد في شركتك</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>الاسم</Label><Input value={accForm.name} onChange={(e)=>setAccForm({...accForm, name:e.target.value})} data-testid="acc-name-input" /></div>
            <div><Label>الإيميل</Label><Input value={accForm.email} onChange={(e)=>setAccForm({...accForm, email:e.target.value})} dir="ltr" className="font-mono" data-testid="acc-email-input" /></div>
            <div><Label>كلمة المرور</Label><Input type="password" value={accForm.password} onChange={(e)=>setAccForm({...accForm, password:e.target.value})} data-testid="acc-password-input" /></div>
            <div><Label>الهاتف</Label><Input value={accForm.phone} onChange={(e)=>setAccForm({...accForm, phone:e.target.value})} dir="ltr" className="font-mono" data-testid="acc-phone-input" /></div>
            <div className="text-[11px] text-muted-foreground bg-muted rounded p-2">سيتمكن المحاسب من تسجيل الدخول بهذا الحساب. عند تعيينه لشركة عميل — سيرى بياناتها المحاسبية ويمكنه إدارتها.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAccOpen(false)}>إلغاء</Button>
            <Button onClick={saveAcc} data-testid="save-acc-btn">إضافة</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={asnOpen} onOpenChange={setAsnOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>تعيين محاسب لشركة عميلة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الشركة العميلة</Label>
              <Select value={asnForm.client_company_id} onValueChange={(v)=>setAsnForm({...asnForm, client_company_id:v})}>
                <SelectTrigger data-testid="assign-client-select"><SelectValue placeholder="اختر شركة..." /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>المحاسب</Label>
              <Select value={asnForm.accountant_id} onValueChange={(v)=>setAsnForm({...asnForm, accountant_id:v})}>
                <SelectTrigger data-testid="assign-acc-select"><SelectValue placeholder="اختر محاسب..." /></SelectTrigger>
                <SelectContent>{accountants.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.assignments} شركة)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>الرسوم الشهرية</Label><Input type="number" value={asnForm.monthly_fee} onChange={(e)=>setAsnForm({...asnForm, monthly_fee:+e.target.value})} data-testid="assign-fee-input" /></div>
              <div>
                <Label>العملة</Label>
                <Select value={asnForm.currency} onValueChange={(v)=>setAsnForm({...asnForm, currency:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["USD","SYP","TRY","EUR"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>ملاحظة</Label><Input value={asnForm.note} onChange={(e)=>setAsnForm({...asnForm, note:e.target.value})} data-testid="assign-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAsnOpen(false)}>إلغاء</Button>
            <Button onClick={saveAssign} data-testid="save-assign-btn">تعيين</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
